import type { ChallengeType, ScoringRules } from '../api/types';

// Which scoring keys mean anything for which challenge type.
//
// `auto_approve` only does something for QUIZ and QR_SCAN — those are the two
// the backend can actually score on its own (services/auto_score.py). On any
// other type the flag is inert: the challenge still waits for a coordinator
// while the editor shows a ticked box claiming otherwise.
//
// That state was reachable because the create form keeps its scoring_rules
// across a type switch: configure a quiz, change your mind, save as text — and
// the quiz keys ride along. App#459.
const TYPE_SPECIFIC_KEYS: Partial<Record<ChallengeType, readonly string[]>> = {
  quiz: ['auto_approve', 'options', 'correct_option'],
  qr_scan: ['auto_approve', 'expected_value'],
};

/** Keys that are meaningful for every challenge type. */
const UNIVERSAL_KEYS: readonly string[] = ['max_points', 'time_bonus'];

/**
 * Drop scoring keys that carry no meaning for `type`.
 *
 * Silent by design: switching type is a normal editing move, not a mistake, so
 * it should not raise a warning — it should just leave the challenge in a state
 * that matches what the form is showing.
 */
export function pruneScoringRules(
  type: ChallengeType | null,
  rules: ScoringRules,
): ScoringRules {
  if (!type) return rules;
  const allowed = new Set([...UNIVERSAL_KEYS, ...(TYPE_SPECIFIC_KEYS[type] ?? [])]);
  const pruned: ScoringRules = {};
  for (const [key, value] of Object.entries(rules)) {
    if (allowed.has(key)) pruned[key] = value;
  }
  return pruned;
}

/** Whether the auto-approve affordance should be offered for this type at all. */
export function supportsAutoApprove(type: ChallengeType | null): boolean {
  return type === 'quiz' || type === 'qr_scan';
}
